import React, {
  ForwardRefRenderFunction,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button, Descriptions, Drawer, Modal, Spin, Tag } from 'antd';
import Form, { FormSchema } from '@sezenta/antd-schema-form';
import { DayJs, Utils } from '../../common/Utils';
import {
  CashActivity,
  useBusinessContext,
} from '../../context/BusinessContext';
import { useConnection, useUser } from '../../connection/connection';
import { useEvents } from '../../common/EventContext';
import { OptionedInputValue } from '../../common/OptionedInput';
import { useCustomRates } from '../../common/CustomRates';

export type ViewIncomeModalInstance = {
  openTx: (txId: number) => void;
};

type Props = {};

const ViewIncomeModal: ForwardRefRenderFunction<
  ViewIncomeModalInstance,
  Props
> = (props, ref) => {
  const [visible, setVisible] = useState<boolean>(false);
  const business = useBusinessContext();
  const connection = useConnection();
  const [saving, setSaving] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [incomeId, setIncomeId] = useState<number>();
  const [title, setTitle] = useState<string>('Create new income');
  const [income, setIncome] = useState<any>();
  const user = useUser();
  const [buttonLoadingStates, setButtonLoadingStates] = useState<{
    [key: string]: boolean;
  }>({});
  const events = useEvents();

  useImperativeHandle(
    ref,
    () => ({
      openTx: (txId: number) => {
        setVisible(true);
        setLoading(true);
        setIncomeId(txId);
        setTitle('View Income');
        connection
          .get(`incomes/${business.businessId}/${txId}`)
          .then((res) => {
            setIncome(res.data);
          })
          .finally(() => setLoading(false));
      },
    }),
    [connection, business.businessId],
  );
  const onChangeState = useCallback(
    (state: string) => {
      setButtonLoadingStates((prev) => ({ ...prev, [state]: true }));
      // setStateLoading(true);
      connection
        .patch(`incomes/status/${business.businessId}/${incomeId}`, {
          status: state,
        })
        .then((res) => {
          if (res.data === 'Success' && income) {
            const temp = income;
            temp.status = state;
            setIncome(temp);
            events.emit('INCOMES', { action: 'UPDATE', data: temp });
          }
        })
        .catch((e) => console.log(e))
        // .finally(() => setStateLoading(false));
        .finally(() => {
          setButtonLoadingStates((prev) => ({ ...prev, [state]: false }));
        });
    },
    [business.businessId, connection, events, income, incomeId],
  );

  return (
    <Drawer
      title={title}
      open={visible}
      onClose={() => setVisible(false)}
      destroyOnClose={true}
      footer={null}
      width={600}
    >
      <Spin spinning={loading}>
        <div className="flex gap-4 mt-5 pt-2 pb-4 flex-wrap">
          {business.states[
            income?.status ?? '0194bdf7-8f0a-7cca-9469-a2985f7c24a1'
          ]?.workflow?.map((t: any) => {
            if (
              t.permissions.every((permission: any) =>
                user.permissions.includes(permission),
              )
            ) {
              return (
                <Button
                  variant="solid"
                  // loading={stateLoading}
                  loading={buttonLoadingStates[t.state] || false}
                  key={t.buttonText}
                  color={'default'}
                  onClick={() => onChangeState(t.state)}
                >
                  {t.buttonText?.toUpperCase()}
                </Button>
              );
            } else {
              <div key={t.buttonText} />;
            }
          })}
        </div>
        {income && (
          <Descriptions
            bordered
            column={1}
            size="small"
            labelStyle={{ fontWeight: 'bold' }}
          >
            <Descriptions.Item label="ID">{income.id}</Descriptions.Item>
            {income.refNo && (
              <Descriptions.Item label="Reference">
                {income.refNo}
              </Descriptions.Item>
            )}
            {income.account && (
              <Descriptions.Item label="Account">
                {business.accounts[income.account]?.name}
              </Descriptions.Item>
            )}
            <Descriptions.Item label="Amount">
              {Utils.formatPrice(income.amount)} {business.currency || ''}
            </Descriptions.Item>
            <Descriptions.Item label="Date">{income.date}</Descriptions.Item>
            <Descriptions.Item label="Description">
              {income.description}
            </Descriptions.Item>
            <Descriptions.Item label="Site">
              {business.sites[income.siteId]?.name}
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={business.states[income.status]?.color || 'blue'}>
                {business.states[income.status]?.name || income.status}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="VAT Amount">
              {Utils.formatPrice(income.vatAmount)} {business.currency || ''}
            </Descriptions.Item>
            <Descriptions.Item label="VAT Type">
              {income.vatType ?? '-'}
            </Descriptions.Item>
            <Descriptions.Item label="IPA Number">
              {income.ipaNo ?? '-'}
            </Descriptions.Item>
            <Descriptions.Item label="IPA Amount">
              {Utils.formatPrice(income.ipaAmount)} {business.currency || ''}
            </Descriptions.Item>
            <Descriptions.Item label="IPC Number">
              {income.ipcNo ?? '-'}
            </Descriptions.Item>
            <Descriptions.Item label="IPC Amount">
              {Utils.formatPrice(income.ipcAmount)} {business.currency || ''}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Spin>
    </Drawer>
  );
};

export const useViewIncomesModal = () => useRef<ViewIncomeModalInstance>(null);

export default React.forwardRef<ViewIncomeModalInstance, Props>(
  ViewIncomeModal,
);
